import { Clock, Database, RefreshCw, ShieldCheck, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { microAppService } from "@/lib/services";
import type { MicroAppInfo } from "@/types";
import { formatRelativeTime as formatWhen } from "./relativeTime";

function InfoRow({
  icon,
  label,
  children,
}: Readonly<{ icon: React.ReactNode; label: string; children: React.ReactNode }>) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-0.5 text-content-tertiary shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-content-tertiary">{label}</p>
        <div className="text-sm text-content-primary">{children}</div>
      </div>
    </div>
  );
}

function ProvenanceSection({ info }: Readonly<{ info: MicroAppInfo }>) {
  if (!info.isLive) {
    return <p className="text-sm text-content-secondary">This is a static dashboard (no live data sources).</p>;
  }
  return (
    <>
      <InfoRow icon={<Database className="h-4 w-4" />} label="Data sources">
        <ul className="space-y-0.5">
          {info.dataSources.map((s) => (
            <li key={s.provider}>
              {s.label}
              <span className="text-content-tertiary">
                {" "}
                · {s.bindingCount} {s.bindingCount === 1 ? "field" : "fields"}
              </span>
            </li>
          ))}
        </ul>
      </InfoRow>
      {info.refreshLabel && (
        <InfoRow icon={<RefreshCw className="h-4 w-4" />} label="Refreshes">
          {info.refreshLabel} (on open)
        </InfoRow>
      )}
      <InfoRow icon={<Clock className="h-4 w-4" />} label="Data last updated">
        {formatWhen(info.lastUpdated)}
      </InfoRow>
      {info.authSummary && (
        <InfoRow icon={<ShieldCheck className="h-4 w-4" />} label="Access">
          {info.authSummary}
        </InfoRow>
      )}
    </>
  );
}

/** "Info / About this dashboard" modal — identity, authorship, and provenance. */
export function MicroAppInfoModal({
  appUuid,
  isOpen,
  onClose,
}: Readonly<{ appUuid: string; isOpen: boolean; onClose: () => void }>) {
  const [info, setInfo] = useState<MicroAppInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setLoading(true);
    setError(false);
    microAppService
      .getInfo(appUuid)
      .then((res) => {
        if (active) setInfo(res.data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isOpen, appUuid]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary truncate">
            {info?.name ? `About “${info.name}”` : "About this dashboard"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-interactive-active rounded-lg transition"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-content-tertiary" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          {loading && <p className="text-sm text-content-secondary">Loading…</p>}
          {error && <p className="text-sm text-status-error">Couldn't load dashboard info.</p>}
          {info && !loading && (
            <>
              {info.about && <p className="text-sm text-content-secondary leading-relaxed">{info.about}</p>}

              <div className="border-t border-border-secondary pt-2">
                <InfoRow icon={<User className="h-4 w-4" />} label="Published by">
                  {info.createdByName || "Unknown"}
                  {info.generatedFromChat && <span className="text-content-tertiary"> · generated from a chat</span>}
                </InfoRow>
                <InfoRow icon={<Clock className="h-4 w-4" />} label="Published">
                  {formatWhen(info.publishedAt)}
                </InfoRow>
              </div>

              <div className="border-t border-border-secondary pt-2">
                <ProvenanceSection info={info} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
